import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withXsrfConfiguration,
} from '@angular/common/http';
import localesPt from '@angular/common/locales/pt';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  importProvidersFrom,
} from '@angular/core';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideAutorizacao } from '@shared/infra/autorizacao/autorizacao.provider';
import { HttpErrorInterceptor } from '@shared/infra/interceptors/HttpErrorInterceptor';
import { provideWebsockets } from '@shared/infra/websockets/websockets.provider';
import { bloqueioInterceptor } from '@shared/infra/bloqueio/bloqueio.interceptor';
import { provideVex } from '@vex/vex.provider';
import { provideNgxMask } from 'ngx-mask';
import { provideQuillConfig } from 'ngx-quill';
import { appRoutes } from './app.routes';
import { provideSliceAuth } from './core/autenticacao/slice-auth.provider';
import { provideIcons } from './core/icons/icons.provider';
import { provideLuxon } from './core/luxon/luxon.provider';
import { provideNavigation } from './core/navigation/navigation.provider';
import { loaderInterceptor } from './shared/components/loader/loader.interceptor';
import { sliceVexConfigs } from './shared/config/slice-configs';

registerLocaleData(localesPt, 'pt-BR');

export const appConfig: ApplicationConfig = {
  providers: [
    provideSliceAuth(),
    provideAutorizacao(),
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    importProvidersFrom(BrowserModule, MatBottomSheetModule, MatDialogModule),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      })
    ),
    provideNgxMask(),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([loaderInterceptor, bloqueioInterceptor]),
      withInterceptorsFromDi(),
      withXsrfConfiguration({})
    ),
    provideVex({
      /**
       * The config that will be used by default.
       * This can be changed at runtime via the config panel or using the VexConfigService.
       */
      config: sliceVexConfigs.zeus,
      /**
       * Only themes that are available in the config in tailwind.config.ts should be listed here.
       * Any theme not listed here will not be available in the config panel.
       */
      availableThemes: [
        {
          name: 'Default',
          className: 'vex-theme-default',
        },
        {
          name: 'Teal',
          className: 'vex-theme-teal',
        },
        {
          name: 'Green',
          className: 'vex-theme-green',
        },
        {
          name: 'Purple',
          className: 'vex-theme-purple',
        },
        {
          name: 'Red',
          className: 'vex-theme-red',
        },
        {
          name: 'Orange',
          className: 'vex-theme-orange',
        },
      ],
    }),
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'BRL' },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { dateFormat: 'shortDate' },
    },
    provideNavigation(),
    provideIcons(),
    provideLuxon(),
    provideLuxonDateAdapter(),
    provideQuillConfig({
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['clean'],
          ['link', 'image'],
        ],
      },
    }),
    provideWebsockets(),
  ],
};
